import {compact} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assertAndContinue, assert} from 'Asserts';
import {
  BenchlingExpression,
  BenchlingExpressionConstantValue,
  BenchlingExpressionFunction,
  BenchlingExpressionSchemaLookup,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {BenchlingExpressionFunctionName} from 'benchling-expressions/BenchlingFunctionSignatures';
import BenchlingConfig from 'BenchlingConfig';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartEdgeConfigSpec, RouterFunctionSpec} from 'processes/dao/ProcessSchemaDAO';
import {OUTPUT_NODE_CLIENT_ID} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  BOOLEAN_OPERATORS_BY_FUNCNAME,
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
} from 'processes/modals/router-config/RouterConfigConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {DeepPartial} from 'util/Types';

export const ROUTER_NODE_ICON = 'decision-node';
export const END_NODE_ICON = 'do-not-enter';

export const NODE_DETAILS_TYPE_TO_ICON: {
  [nodeDetailsType in ProcessFlowchartNodeDetailsType]?: BenchlingIconName;
} = {
  [ProcessFlowchartNodeDetailsType.END_NODE_DETAILS]: END_NODE_ICON,
  [ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS]: ROUTER_NODE_ICON,
};

export function canRouteToNode(node: ProcessGraphBuilderNode): boolean {
  return (
    node.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    node.type === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  );
}

export function getPartialExpression(
  expression?: DeepPartial<BenchlingExpression> | null
): DeepPartial<BenchlingExpressionFunction> | null {
  if (expression == null) {
    return null;
  }
  assert(expression.type === BenchlingExpressionType.FUNCTION, 'Route must be of type function or unset');
  return expression;
}

export function getPartialConstant(
  initialFunction: DeepPartial<BenchlingExpressionFunction> | null
): DeepPartial<BenchlingExpressionConstantValue> | null {
  const constantParameter = initialFunction?.parameters?.[1] ?? null;
  const constantExpression = constantParameter?.expression;
  if (constantExpression == null) {
    return null;
  }
  assert(
    constantExpression.type === BenchlingExpressionType.CONSTANT_VALUE,
    'Right hand side of route must be a constant or unset'
  );
  return constantExpression;
}

export function getPartialLookup(
  initialFunction: DeepPartial<BenchlingExpressionFunction> | null
): DeepPartial<BenchlingExpressionSchemaLookup> | null {
  const lookupParameter = initialFunction?.parameters?.[0] ?? null;
  const lookup = lookupParameter?.expression;
  if (lookup == null) {
    return null;
  }
  assert(
    lookup.type === BenchlingExpressionType.SCHEMA_LOOKUP,
    'Left hand side of route must be a schema lookup or unset'
  );
  return lookup;
}

export function expressionFunctionIsBoolean(
  expression: DeepPartial<BenchlingExpressionFunction> | null
): boolean {
  if (expression == null || expression?.name === undefined) {
    return false;
  }

  return BOOLEAN_OPERATORS_BY_FUNCNAME[expression.name] !== undefined;
}

export function convertToRouterConfigSpec(
  validatedSpec: CreateOrUpdateRouterFunctionSpec,
  flowchartSpecHasEdgeToOutput: boolean = false
): RouterFunctionSpec {
  assert(
    (validatedSpec.expression == null) === validatedSpec.isDefault,
    'Only the default route can have a null expression'
  );
  let edgeConfigReference: ProcessFlowchartEdgeConfigSpec | null | undefined;
  if (
    assertAndContinue(
      flowchartSpecHasEdgeToOutput ||
        validatedSpec.edgeConfigReference?.toNodeClientId !== OUTPUT_NODE_CLIENT_ID,
      'Expected edge references to output to be null when the flowchart does not have root and output nodes'
    )
  ) {
    edgeConfigReference = validatedSpec.edgeConfigReference;
  } else {
    edgeConfigReference = null;
  }
  if (validatedSpec.isDefault) {
    return {
      name: validatedSpec.name,
      edgeConfigClientId: edgeConfigReference?.clientId ?? null,
      expressionJson: null,
    };
  }
  return {
    name: validatedSpec.name,
    edgeConfigClientId: edgeConfigReference?.clientId ?? null,
    expressionJson: validatedSpec.expression as BenchlingExpression,
  };
}

function wereFunctionExpressionsUpdated(
  functionBefore: CreateOrUpdateRouterFunctionSpec,
  functionAfter: CreateOrUpdateRouterFunctionSpec
): boolean {
  return !isEqual(
    functionBefore.expression == null && !functionBefore.isDefault
      ? {type: BenchlingExpressionType.FUNCTION}
      : functionBefore.expression,
    functionAfter.expression
  );
}

function wereFunctionsUpdated(
  functionsBefore: Array<CreateOrUpdateRouterFunctionSpec>,
  functionsAfter: Array<CreateOrUpdateRouterFunctionSpec>
): boolean {
  if (functionsBefore.length !== functionsAfter.length) {
    return true;
  }
  for (let i = 0; i < functionsBefore.length; i++) {
    const functionBefore = functionsBefore[i];
    const functionAfter = functionsAfter[i];
    if (functionBefore.name !== functionAfter.name) {
      return true;
    }
    if (!isEqual(functionBefore.edgeConfigReference, functionAfter.edgeConfigReference)) {
      return true;
    }
    if (wereFunctionExpressionsUpdated(functionBefore, functionAfter)) {
      return true;
    }
  }
  return false;
}

export function wasInitialRouterConfigUnconfigured(initialRouterConfig: RouterConfig): boolean {
  return [...initialRouterConfig.functionSpecs, initialRouterConfig.defaultFunctionSpec].every(
    (functionSpec) => functionSpec == null || !functionSpec.isConfigured
  );
}

export function wereRouterConfigFunctionsUpdated(
  initialRouterConfig: RouterConfig,
  newDefaultFunctionSpec: CreateOrUpdateRouterFunctionSpec | null,
  newFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec>
): boolean {
  const wasDefaultFunctionUpdatedCheck = wereFunctionsUpdated(
    _.compact([initialRouterConfig.defaultFunctionSpec]),
    _.compact([newDefaultFunctionSpec])
  );
  const wereFunctionsUpdatedCheck = wereFunctionsUpdated(initialRouterConfig.functionSpecs, newFunctionSpecs);
  return wasDefaultFunctionUpdatedCheck || wereFunctionsUpdatedCheck;
}

export function wasRouterConfigUpdated(
  initialRouterConfig: RouterConfig,
  newName: string,
  newDefaultFunctionSpec: CreateOrUpdateRouterFunctionSpec | null,
  newFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec>
): boolean {
  const wasNameUpdatedCheck = newName !== initialRouterConfig.name;
  const wereFunctionsUpdatedCheck = wereRouterConfigFunctionsUpdated(
    initialRouterConfig,
    newDefaultFunctionSpec,
    newFunctionSpecs
  );
  return wasNameUpdatedCheck || wereFunctionsUpdatedCheck;
}

/**
 * Recursively gets an array of all schemas referenced in the given expression. If the expression is not a function,
 * then there is no schema lookup. If the expression is not an `AND` or `OR`, then there should be a single schema lookup.
 * If the expression is an `AND` or `OR`, then each parameter is a separate expression that should be checked for schemas.
 *
 * @param expression The expression to search for schemas in.
 * @returns An array of schema id strings referenced in the expression.
 */
function getSchemasReferencedInExpression(
  expression?: BenchlingExpression | DeepPartial<BenchlingExpression> | null
): Array<string | null> {
  if (expression == null) {
    return [];
  }

  if (expression?.type !== BenchlingExpressionType.FUNCTION) {
    return [];
  } else if (
    expression?.name !== BenchlingExpressionFunctionName.AND &&
    expression?.name !== BenchlingExpressionFunctionName.OR
  ) {
    // If the expression is not an AND or OR, then it should have a single schema that can be looked up
    const lookup = getPartialLookup(expression);
    return [lookup?.schema ?? null];
  }

  const params = expression?.parameters ?? [];

  // If it's an AND or OR, then we recursively search for schemas in the parameters and concatenate the result
  return params.reduce(
    (prev: Array<string | null>, curr) =>
      prev.concat(getSchemasReferencedInExpression(getPartialExpression(curr?.expression) ?? null)),
    []
  );
}

/**
 * Gets all schemas referenced in the given router config, from all function specs
 * @param routerConfig the router config to search for schemas in
 * @returns An array of schema id strings
 */
export function getSchemasReferencedInRoutingRules(routerConfig: RouterConfig): Array<string> {
  if (BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS) {
    return [
      ...new Set(
        compact(
          [...routerConfig.functionSpecs, routerConfig.defaultFunctionSpec]
            .map((functionSpec) => {
              if (functionSpec?.expression == null) {
                return null;
              }
              return getSchemasReferencedInExpression(getPartialExpression(functionSpec.expression));
            })
            .flat()
        )
      ),
    ];
  }
  return [
    ...new Set(
      compact(
        [...routerConfig.functionSpecs, routerConfig.defaultFunctionSpec].map((functionSpec) => {
          if (functionSpec?.expression == null) {
            return null;
          }
          const schemaLookup = getPartialLookup(getPartialExpression(functionSpec.expression));
          return schemaLookup?.schema ?? null;
        })
      )
    ),
  ];
}
